import React, {
  createContext,
  startTransition,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import type { AuthChangeEvent, Session, User as SupabaseUser } from '@supabase/supabase-js';
import { sessionRequests } from './sessionRequests';
import { SUPABASE_AUTH_STORAGE_KEY } from '../supabaseConfig';
import { getVerifiedSession } from './verifiedSession';
import { User, SessionState, SessionEvents } from './types';
import { notifyActiveRunAuthEvent } from './activeRunIdentity';
import { completeGoogleSignupEmailPreference } from './signupEmailPreference';
import { completeGoogleSignupTermsAcceptance } from './signupTermsAcceptance';

const SessionContext = createContext<SessionState | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useSession = (): SessionState => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionManagerProvider');
  }
  return context;
};

/** Returns auth state when mounted below AuthProvider, or null in isolated tests/tools. */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalSession = (): SessionState | null => useContext(SessionContext) ?? null;

interface SessionManagerProviderProps extends SessionEvents {
  children: ReactNode;
  deferSessionDiscovery?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export function mapSupabaseUser(supabaseUser: SupabaseUser): User {
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || undefined,
    name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || undefined,
    user_metadata: supabaseUser.user_metadata,
  };
}

/** A hint only decides when to load the SDK, never whether an identity is valid. */
function hasSessionToDiscover(): boolean {
  if (!SUPABASE_AUTH_STORAGE_KEY) return true;
  try {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (
      [query, hash].some((params) =>
        ['code', 'access_token', 'error', 'error_description'].some((key) => params.has(key))
      )
    )
      return true;
    return (
      localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY) !== null ||
      localStorage.getItem(`${SUPABASE_AUTH_STORAGE_KEY}-code-verifier`) !== null
    );
  } catch {
    // Restricted storage cannot prove absence. Let the canonical SDK resolve it.
    return true;
  }
}

export function SessionManagerProvider({
  children,
  onAuthStateChange,
  onPasswordRecovery,
  deferSessionDiscovery = false,
}: SessionManagerProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [requestsReady, setRequestsReady] = useState(false);
  const [authRevision, setAuthRevision] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const retryRef = useRef<() => Promise<void>>(async () => {});
  const discoverRef = useRef<() => Promise<void>>(async () => {});
  const initiallyDeferred = useRef(deferSessionDiscovery);

  // Store callbacks in refs to avoid re-triggering the effect
  const callbackRef = useRef(onAuthStateChange);
  callbackRef.current = onAuthStateChange;
  const passwordRecoveryRef = useRef(onPasswordRecovery);
  passwordRecoveryRef.current = onPasswordRecovery;

  // Initialize auth state from Supabase session
  useEffect(() => {
    let isActive = true;
    let subscription: { unsubscribe: () => void } | null = null;
    let authTransitionVersion = 0;

    let bootstrapVersion = 0;

    const bootstrap = async () => {
      const attempt = ++bootstrapVersion;
      const isCurrent = () => isActive && attempt === bootstrapVersion;
      subscription?.unsubscribe();
      subscription = null;
      setLoading(true);
      setAuthResolved(false);
      setRequestsReady(false);
      setError(null);
      retryRef.current = bootstrap;

      let clientModule: typeof import('../supabase');
      try {
        clientModule = await import('../supabase');
      } catch (err) {
        if (!isCurrent()) return;
        // Capability failure is unresolved auth, never a verified guest session.
        startTransition(() => {
          setError(err instanceof Error ? err : new Error('Unable to load your session.'));
          setLoading(false);
        });
        return;
      }
      if (!isCurrent()) return;
      const { supabase } = clientModule;

      const resolveTransition = async (event: AuthChangeEvent, session: Session | null) => {
        const version = ++authTransitionVersion;
        const revision = notifyActiveRunAuthEvent();
        setAuthRevision(revision);
        setRequestsReady(false);
        setError(null);
        const newUser = session?.user ? mapSupabaseUser(session.user) : null;
        setUser(newUser);
        retryRef.current = () => resolveTransition(event, session);
        try {
          await sessionRequests.transition(session, () => supabase.auth.getSession());
          if (!isCurrent() || version !== authTransitionVersion) return;
          setUser(newUser);
          setRequestsReady(true);
          setAuthResolved(true);
          setLoading(false);
          callbackRef.current?.(event, newUser);
          if (event === 'PASSWORD_RECOVERY') passwordRecoveryRef.current?.();
        } catch (err) {
          if (!isCurrent() || version !== authTransitionVersion) return;
          // Keep the last identity blocked. A temporary unreadable token must not
          // turn an authenticated player into a new anonymous player.
          setError(err instanceof Error ? err : new Error('Unable to refresh your session.'));
          setLoading(false);
        }
      };

      const verificationStartTransitionVersion = authTransitionVersion;
      const {
        data: { subscription: authSubscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        if (!isCurrent()) return;
        // Bootstrap is verified below before it becomes usable. A later SDK
        // INITIAL_SESSION must not supersede a newer sign-in/sign-out transition.
        if (event !== 'INITIAL_SESSION') void resolveTransition(event, session);
        void import('@/lib/telemetry')
          .then(({ trackBreadcrumb }) => trackBreadcrumb('auth', event))
          .catch(() => undefined);
      });
      subscription = authSubscription;

      const initializeAuth = async () => {
        if (!isCurrent() || authTransitionVersion !== verificationStartTransitionVersion) return;

        try {
          const session = await getVerifiedSession();

          if (!isCurrent()) return;

          // A sign-in or sign-out can occur while the persisted session is being
          // verified. Keep that newer transition instead of restoring a stale
          // verification snapshot. INITIAL_SESSION is not a newer transition,
          // so verification still clears any invalid persisted user it surfaced.
          if (authTransitionVersion === verificationStartTransitionVersion) {
            sessionRequests.publishVerified(session);
            retryRef.current = () => resolveTransition('TOKEN_REFRESHED', session);
            const mappedUser = session?.user ? mapSupabaseUser(session.user) : null;
            const revision = session?.user ? notifyActiveRunAuthEvent() : null;
            // Initial verification must preserve prerendered Suspense content
            // while its code hydrates. Request ownership is already published;
            // later sign-in/out transitions retain their immediate identity fence.
            startTransition(() => {
              if (revision !== null) setAuthRevision(revision);
              setUser(mappedUser);
              setRequestsReady(true);
              setAuthResolved(true);
              setLoading(false);
            });
            if (session?.user) {
              callbackRef.current?.('INITIAL_SESSION', mappedUser);
            }
          }
        } catch (err) {
          console.error('Auth initialization error:', err);
          if (!isCurrent() || authTransitionVersion !== verificationStartTransitionVersion) return;
          sessionRequests.publishVerified(null);
          // Authentication has reached a terminal result even though verification failed.
          // Public experiences can continue anonymously and protected surfaces can render
          // their actionable unauthorized state instead of remaining on a skeleton forever.
          startTransition(() => {
            setRequestsReady(true);
            setError(err instanceof Error ? err : new Error('Unknown auth initialization error'));
            setAuthResolved(true);
            setLoading(false);
          });
        }
      };

      await initializeAuth();
    };

    let discoveryStarted = false;
    const startDiscovery = () => {
      if (!isActive || discoveryStarted) return Promise.resolve();
      discoveryStarted = true;
      window.removeEventListener('storage', onStorage);
      return bootstrap();
    };
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === SUPABASE_AUTH_STORAGE_KEY ||
        event.key?.startsWith(`${SUPABASE_AUTH_STORAGE_KEY}-`)
      )
        void startDiscovery();
    };
    discoverRef.current = startDiscovery;
    retryRef.current = startDiscovery;
    // Listen before checking storage, so a cross-tab sign-in cannot fall in a gap.
    window.addEventListener('storage', onStorage);
    if (!initiallyDeferred.current || hasSessionToDiscover()) void startDiscovery();

    return () => {
      window.removeEventListener('storage', onStorage);
      discoverRef.current = async () => {};
      isActive = false;
      bootstrapVersion += 1;
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!deferSessionDiscovery) void discoverRef.current();
  }, [deferSessionDiscovery]);

  useEffect(() => {
    if (!user?.id || !authResolved || !requestsReady) return;
    void completeGoogleSignupTermsAcceptance(user.id).catch(() => {
      console.warn('Signup terms acceptance could not be saved.');
    });
    void completeGoogleSignupEmailPreference(user.id).catch(() => {
      // Leave the tab-bound choice available for retry on the next verified session.
      console.warn('Signup email preference could not be saved.');
    });
  }, [user?.id, authResolved, requestsReady]);

  const state: SessionState = {
    user,
    loading,
    authResolved,
    requestsReady,
    authRevision,
    isAuthenticated: !!user,
    error,
    retry: () => retryRef.current(),
  };

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}
